import { useEffect, useMemo, useState } from 'preact/hooks';
import { qs } from '../api';
import { dataCurta, diaIso, inteiro, pct, plural, sinal, tempoRelativo } from '../format';
import { carregarGeo, useApi, useDebounce } from '../hooks';
import { criarEscala } from '../mapa/escalas';
import { Legenda } from '../mapa/Legenda';
import { MapaPB } from '../mapa/MapaPB';
import { MAX_DIAS_ATRAS, camada, diasAtras, municipioSel, pesquisaId, secretariaId } from '../state';
import { tema } from '../tema';
import type { Camada, EventoFeed, MapaResposta, Mesorregiao, Mudancas, PesquisaResumo, PontoSerie, ResumoEstado, SecretariaResumo } from '../types';
import { CartaoIndicador } from './CartaoIndicador';
import { useCamadaIndicador, type CamadaIndicador } from './camadaIndicadores';
import { DrawerMunicipio } from './DrawerMunicipio';
import { GraficoLinha } from './GraficoLinha';
import { eixoSel, indicadorSel } from '../state';

const ROTULO_CAMADA: Record<Camada, string> = {
  progresso: 'Progresso',
  cobertura: 'Cobertura',
  atrasadas: 'Atrasadas',
  satisfacao: 'Satisfação',
  indicadores: 'Indicadores oficiais',
};

const ICONE_EVENTO: Record<EventoFeed['tipo'], { simbolo: string; classe: string; verbo: string }> = {
  criada: { simbolo: '＋', classe: 'ev-azul', verbo: 'Nova ação' },
  concluida: { simbolo: '✓', classe: 'ev-verde', verbo: 'Concluída' },
  reaberta: { simbolo: '↺', classe: 'ev-ambar', verbo: 'Reaberta' },
  cancelada: { simbolo: '✕', classe: 'ev-vermelho', verbo: 'Cancelada' },
  suspensa: { simbolo: '⏸', classe: 'ev-ambar', verbo: 'Suspensa' },
  progresso: { simbolo: '↗', classe: 'ev-azul', verbo: 'Avanço' },
  nota: { simbolo: '✎', classe: 'ev-cinza', verbo: 'Registro' },
};

export function Painel() {
  const dias = useDebounce(diasAtras.value, 250);
  const data = dias === 0 ? undefined : diaIso(dias);
  const sec = secretariaId.value;
  const cam = camada.value;
  const indic = useCamadaIndicador(cam === 'indicadores');

  // ---- consultas ----
  const filtroBase = qs({ data, secretariaId: sec });
  const resumo = useApi<ResumoEstado>(`/painel/resumo${filtroBase}`);
  const mesos = useApi<Mesorregiao[]>(`/painel/mesorregioes${filtroBase}`);
  const secretarias = useApi<SecretariaResumo[]>(`/painel/secretarias${qs({ data })}`);
  const serie = useApi<{ pontos: PontoSerie[] }>(`/painel/serie${qs({ secretariaId: sec })}`);
  const atividade = useApi<{ itens: EventoFeed[] }>(`/painel/atividade${qs({ secretariaId: sec, limite: 14 })}`);
  const mudancas = useApi<Mudancas>(`/painel/mudancas${qs({ secretariaId: sec, ate: data, de: data ? diaIso(dias + 7) : undefined })}`);

  // Pesquisas: só alguns perfis podem listar; para os outros a aba "Satisfação" simplesmente não aparece.
  const pesquisas = useApi<PesquisaResumo[]>('/pesquisas');
  const pesquisasUteis = (pesquisas.dados ?? []).filter((p) => p.status !== 'RASCUNHO');

  useEffect(() => {
    if (cam === 'satisfacao' && !pesquisaId.value && pesquisasUteis[0]) pesquisaId.value = pesquisasUteis[0].id;
  }, [cam, pesquisasUteis.length]);

  // A camada de indicadores oficiais tem a própria fonte de dados (hook acima); as demais vêm de /painel/mapa.
  const caminhoMapa =
    cam === 'indicadores'
      ? null
      : cam === 'satisfacao'
        ? pesquisaId.value
          ? `/painel/mapa${qs({ camada: cam, pesquisaId: pesquisaId.value, data })}`
          : null
        : `/painel/mapa${qs({ camada: cam, data, secretariaId: sec })}`;
  const mapa = useApi<MapaResposta>(caminhoMapa);
  const temaAtual = tema.value; // trocar o tema repinta o mapa e a legenda com a paleta do outro tema
  const escalaPadrao = useMemo(() => criarEscala(cam === 'indicadores' ? 'progresso' : cam, mapa.dados?.escala ?? { min: null, max: null }, temaAtual), [cam, mapa.dados, temaAtual]);
  const escala = cam === 'indicadores' && indic.escala ? indic.escala : escalaPadrao;
  // Evita mostrar dados de outra camada por um instante ao trocar de aba.
  const dadosMapa = cam === 'indicadores' ? indic.dadosMapa : mapa.dados && mapa.dados.camada === cam ? mapa.dados : null;
  const carregandoMapa = cam === 'indicadores' ? indic.carregando : mapa.carregando;
  const erroMapa = cam === 'indicadores' ? indic.erro : mapa.erro;

  const secretariasOpcoes = secretarias.dados ?? [];
  const r = resumo.dados;

  return (
    <div class="painel">
      <header class="topo">
        <div class="marca">
          <strong>MAPAS</strong>
          <span>Ações do governo · Paraíba</span>
        </div>
        <Resumo r={r} aoVivo={dias === 0} />
      </header>

      <main class="grade">
        <aside class="lateral esquerda" aria-label="Evolução e mudanças">
          <section class="cartao">
            <h2>Evolução do progresso</h2>
            <p class="dica">Média das ações, últimos 90 dias</p>
            <GraficoLinha pontos={(serie.dados?.pontos ?? []).map((p) => ({ data: p.data, valor: p.progresso }))} rotulo="Progresso médio" />
          </section>

          <section class="cartao">
            <h2>O que mudou</h2>
            <p class="dica">
              Progresso por município, {mudancas.dados ? `${dataCurta(mudancas.dados.de)} → ${dataCurta(mudancas.dados.ate)}` : 'últimos 7 dias'}
            </p>
            <ListaMudancas m={mudancas.dados} />
          </section>
        </aside>

        <section class="centro" aria-label="Mapa">
          <Filtros pesquisas={pesquisasUteis} secretarias={secretariasOpcoes} indic={indic} />
          <div class="mapa-area">
            <MapaPB camada={cam} dados={dadosMapa} escala={escala} atualizando={carregandoMapa} selecionado={municipioSel.value} onSelecionar={(c) => (municipioSel.value = c)} textoDica={cam === 'indicadores' ? indic.textoDica : undefined} />
            <Legenda escala={escala} dados={dadosMapa} nota={cam === 'indicadores' ? 'Cada cor reúne o mesmo nº de municípios' : undefined} />
            {cam === 'satisfacao' && !pesquisaId.value && <p class="aviso">Não há pesquisa publicada para mostrar.</p>}
            {erroMapa && <p class="aviso erro">{erroMapa.message}</p>}
          </div>
          {cam === 'indicadores' ? <CartaoIndicador indic={indic} /> : <ControleTempo dias={diasAtras.value} />}
        </section>

        <aside class="lateral direita" aria-label="Resumos e atividade">
          <section class="cartao">
            <h2>Por mesorregião</h2>
            <ul class="barras">
              {(mesos.dados ?? []).map((m) => (
                <li key={m.mesorregiao}>
                  <div class="barra-linha">
                    <span>{m.mesorregiao.replace(' Paraibano', '').replace(' Paraibana', '')}</span>
                    <strong>{pct(m.progressoMedio)}</strong>
                  </div>
                  <div class="trilho"><div class="preench" style={{ width: `${m.progressoMedio ?? 0}%` }} /></div>
                  <small>{plural(m.total, 'ação', 'ações')} · {m.municipiosComAcoes}/{m.municipiosTotal} municípios</small>
                </li>
              ))}
            </ul>
          </section>

          <section class="cartao">
            <h2>Por secretaria</h2>
            <ul class="barras">
              {secretariasOpcoes.filter((s) => s.total > 0).slice(0, 6).map((s) => (
                <li key={s.secretariaId}>
                  <div class="barra-linha">
                    <span><i class="ponto" style={{ background: s.cor }} />{s.sigla}</span>
                    <strong>{pct(s.progressoMedio)}</strong>
                  </div>
                  <div class="trilho"><div class="preench" style={{ width: `${s.progressoMedio ?? 0}%`, background: s.cor }} /></div>
                  <small>{plural(s.total, 'ação', 'ações')}{s.atrasadas > 0 ? ` · ${s.atrasadas} atrasada${s.atrasadas > 1 ? 's' : ''}` : ''}</small>
                </li>
              ))}
              {secretariasOpcoes.every((s) => s.total === 0) && <li class="vazio">Nenhuma ação cadastrada ainda.</li>}
            </ul>
          </section>

          <section class="cartao">
            <h2>Últimas atualizações</h2>
            <ul class="feed">
              {(atividade.dados?.itens ?? []).map((e) => {
                const ic = ICONE_EVENTO[e.tipo];
                return (
                  <li key={e.id}>
                    <button type="button" class="feed-item" onClick={() => (municipioSel.value = e.codIbge)}>
                      <span class={`feed-icone ${ic.classe}`} aria-hidden="true">{ic.simbolo}</span>
                      <span class="feed-texto">
                        <strong>{ic.verbo}</strong> · {e.titulo}
                        <small>{e.municipio} · {e.secretaria} · {e.tipo === 'criada' ? '' : `${e.progressoPct}% · `}{tempoRelativo(e.em)}</small>
                      </span>
                    </button>
                  </li>
                );
              })}
              {atividade.dados?.itens.length === 0 && <li class="vazio">Sem atividade recente.</li>}
            </ul>
          </section>
        </aside>
      </main>

      {municipioSel.value && <DrawerMunicipio cod={municipioSel.value} data={data} onFechar={() => (municipioSel.value = null)} />}
    </div>
  );
}

function Resumo({ r, aoVivo }: { r: ResumoEstado | null; aoVivo: boolean }) {
  const t = r?.total ?? 0;
  const parte = (n: number) => (t > 0 ? `${(n / t) * 100}%` : '0%');
  return (
    <section class="resumo" aria-label="Resumo do estado">
      <div class="resumo-numeros">
        <div class="kpi kpi-grande">
          <span class="kpi-valor">{pct(r?.progressoMedio)}</span>
          <span class="kpi-rotulo">progresso médio {aoVivo ? '· ao vivo' : r ? `· em ${dataCurta(r.data, true)}` : ''}</span>
        </div>
        <div class="kpi"><span class="kpi-valor">{inteiro(r?.total)}</span><span class="kpi-rotulo">ações</span></div>
        <div class="kpi"><span class="kpi-valor">{inteiro(r?.concluidas)}</span><span class="kpi-rotulo">concluídas</span></div>
        <div class="kpi kpi-alerta"><span class="kpi-valor">{inteiro(r?.atrasadas)}</span><span class="kpi-rotulo">atrasadas</span></div>
        <div class="kpi"><span class="kpi-valor">{r ? `${r.municipiosComAcoes}/${r.municipiosTotal}` : '—'}</span><span class="kpi-rotulo">municípios atendidos</span></div>
      </div>
      <div class="pilha" role="img" aria-label={r ? `${r.concluidas} concluídas, ${r.emAndamento} em andamento, ${r.planejadas} planejadas, ${r.suspensas} suspensas` : 'Sem dados'}>
        <span class="pilha-concluida" style={{ width: parte(r?.concluidas ?? 0) }} />
        <span class="pilha-andamento" style={{ width: parte(r?.emAndamento ?? 0) }} />
        <span class="pilha-planejada" style={{ width: parte(r?.planejadas ?? 0) }} />
        <span class="pilha-suspensa" style={{ width: parte(r?.suspensas ?? 0) }} />
      </div>
    </section>
  );
}

function Filtros({ pesquisas, secretarias, indic }: { pesquisas: PesquisaResumo[]; secretarias: SecretariaResumo[]; indic: CamadaIndicador }) {
  const camadas: Camada[] = ['progresso', 'cobertura', 'atrasadas', ...(pesquisas.length > 0 ? (['satisfacao'] as const) : []), 'indicadores'];
  return (
    <div class="filtros">
      <div class="abas" role="tablist" aria-label="Camada do mapa">
        {camadas.map((c) => (
          <button key={c} type="button" role="tab" aria-selected={camada.value === c} class={camada.value === c ? 'aba ativa' : 'aba'} onClick={() => (camada.value = c)}>
            {ROTULO_CAMADA[c]}
          </button>
        ))}
      </div>

      {camada.value === 'indicadores' ? (
        <>
          <label class="campo">
            <span>Eixo</span>
            <select value={eixoSel.value} onChange={(e) => { eixoSel.value = e.currentTarget.value; indicadorSel.value = ''; }}>
              {indic.eixos.filter((x) => x.comDados > 0).map((x) => <option key={x.slug} value={x.slug}>{x.nome}</option>)}
            </select>
          </label>
          <label class="campo">
            <span>Indicador</span>
            <select value={indic.slug} onChange={(e) => (indicadorSel.value = e.currentTarget.value)}>
              {indic.indicadores.map((i) => <option key={i.slug} value={i.slug}>{i.nome}</option>)}
            </select>
          </label>
        </>
      ) : camada.value === 'satisfacao' ? (
        <label class="campo">
          <span>Pesquisa</span>
          <select value={pesquisaId.value} onChange={(e) => (pesquisaId.value = e.currentTarget.value)}>
            {pesquisas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.titulo} {p.status === 'ABERTA' ? '(em andamento)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : (
        secretarias.length > 1 && (
          <label class="campo">
            <span>Secretaria</span>
            <select value={secretariaId.value} onChange={(e) => (secretariaId.value = e.currentTarget.value)}>
              <option value="">Todas</option>
              {secretarias.map((s) => (
                <option key={s.secretariaId} value={s.secretariaId}>{s.sigla} — {s.nome}</option>
              ))}
            </select>
          </label>
        )
      )}
      <BuscaMunicipio />
    </div>
  );
}

/** Alternativa por teclado ao clique no mapa: digitar o nome do município abre os detalhes. */
function BuscaMunicipio() {
  const [nomes, setNomes] = useState<{ cod: string; nome: string }[]>([]);
  useEffect(() => {
    carregarGeo().then((g) => setNomes(g.features.map((f) => ({ cod: f.properties.codIbge, nome: f.properties.nome }))));
  }, []);
  return (
    <label class="campo">
      <span>Buscar município</span>
      <input
        list="lista-municipios"
        placeholder="Digite o nome…"
        onChange={(e) => {
          const achou = nomes.find((n) => n.nome.toLowerCase() === e.currentTarget.value.trim().toLowerCase());
          if (achou) {
            municipioSel.value = achou.cod;
            e.currentTarget.value = '';
          }
        }}
      />
      <datalist id="lista-municipios">
        {nomes.map((n) => <option key={n.cod} value={n.nome} />)}
      </datalist>
    </label>
  );
}

function ControleTempo({ dias }: { dias: number }) {
  const data = diaIso(dias);
  return (
    <div class="tempo">
      <label for="tempo">{dias === 0 ? 'Ao vivo' : `Estado em ${dataCurta(data, true)}`}</label>
      <input
        id="tempo"
        type="range"
        min={-MAX_DIAS_ATRAS}
        max={0}
        step={1}
        value={-dias}
        onInput={(e) => (diasAtras.value = -Number(e.currentTarget.value))}
        aria-valuetext={dias === 0 ? 'Ao vivo' : `${dias} dias atrás, ${dataCurta(data, true)}`}
      />
      <div class="tempo-extremos">
        <span>{dataCurta(diaIso(MAX_DIAS_ATRAS))}</span>
        <button type="button" class={dias === 0 ? 'link ativo' : 'link'} onClick={() => (diasAtras.value = 0)} disabled={dias === 0}>
          ● Voltar ao vivo
        </button>
      </div>
    </div>
  );
}

function ListaMudancas({ m }: { m: Mudancas | null }) {
  if (!m) return <p class="vazio">Carregando…</p>;
  if (m.altas.length === 0 && m.quedas.length === 0) {
    return <p class="vazio">Nenhum município mudou de progresso neste período.</p>;
  }
  const linha = (x: Mudancas['altas'][number], positivo: boolean) => (
    <li key={x.codIbge}>
      <button type="button" class="mudanca" onClick={() => (municipioSel.value = x.codIbge)}>
        <span>{x.municipio}</span>
        <span class="mudanca-faixa"><i class={positivo ? 'sobe' : 'desce'} style={{ width: `${Math.min(100, Math.abs(x.delta) * 2)}%` }} /></span>
        <strong class={positivo ? 'sobe-txt' : 'desce-txt'}>{sinal(x.delta)}</strong>
      </button>
    </li>
  );
  return (
    <>
      {m.altas.length > 0 && <ul class="mudancas">{m.altas.slice(0, 5).map((x) => linha(x, true))}</ul>}
      {m.quedas.length > 0 && <ul class="mudancas">{m.quedas.slice(0, 5).map((x) => linha(x, false))}</ul>}
      {m.municipiosNovos > 0 && <p class="dica">{plural(m.municipiosNovos, 'município começou', 'municípios começaram')} a ter ações no período.</p>}
    </>
  );
}
